// React
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
// cuid
import cuid from "cuid";
// i18n
import { withNamespaces } from "react-i18next";
// Style
import styles from "./BlockLoader.css";
// Components
import Footer from "./components/Footer";
import Item from "./components/Item";
import Modal from "../../../../../components/Modal";
import NoResults from "./components/NoResults";
import SelectExamParts from "./components/SelectExamParts";
import Notice from "../../../../../components/Notice";

// Ducks
import { navigateTo } from "../../../Dashboard.ducks";
import { clearMissingExamsParts, setMissingExamsParts, setSelectedDate } from "../../Exams.ducks";

class BlockLoader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      isProceedButtonDisabled: true,
      remainingAvailableTime: undefined,
      selectedOption: undefined,
      showModal: false
    };
    this.pageSize = 15;
  }

  static getDerivedStateFromProps(props, state) {
    if (!props.rows.length && state.currentPage > 1) {
      return {
        currentPage: 1
      };
    }
    return null;
  }

  handleBooking = ({ missingExamParts, selection }) => {
    const { navigateTo, setMissingExamsParts, setSelectedDate } = this.props;

    setSelectedDate(selection);
    if (missingExamParts) {
      setMissingExamsParts(missingExamParts);
    }
    navigateTo(`/dashboard/details`);
  };

  handleCheckExamPart = (examPartId) => (event) => {
    const { selectedOption } = this.state;

    /**
     * For each examination part set the checked flag to know is the part is selected or not
     */
    const newSelectedExaminationParts = selectedOption.examinationParts.map((part) => {
      if (part.examPartId === examPartId) {
        // eslint-disable-next-line no-param-reassign
        part.checked = event.target.checked;
      }
      return part;
    });

    /**
     * Create a new selected option object with the updated examination parts
     * for immutability purposes
     */
    const newSelectedOption = {
      ...selectedOption,
      examinationParts: newSelectedExaminationParts
    };

    /**
     * Calculate the remaining available time to schedule the examination parts
     */
    const newRemainingAvailableTime = newSelectedExaminationParts.reduce((acc, part) => {
      if (part.checked) {
        // eslint-disable-next-line no-param-reassign
        acc -= part.examDuration;
      }
      return acc;
    }, selectedOption.longestNonOccupiedTimeSpan);

    /**
     * Check if the proceed button should be disabled
     */
    const isProceedButtonDisabled =
      !newSelectedExaminationParts.some((part) => part.checked) && newRemainingAvailableTime >= 0;

    this.setState({
      isProceedButtonDisabled,
      remainingAvailableTime: newRemainingAvailableTime,
      selectedOption: newSelectedOption
    });
  };

  handleRowClick = (selectedOption) => (event) => {
    const { clearMissingExamsParts } = this.props;

    event.stopPropagation();

    const remainingAvailableTime = selectedOption.longestNonOccupiedTimeSpan;

    this.setState({ selectedOption, remainingAvailableTime }, () => {
      const numberOfParts = selectedOption.examinationParts.length;
      const { maxBookedExams } = selectedOption;

      clearMissingExamsParts();

      return numberOfParts !== maxBookedExams
        ? this.toggleModal()
        : this.handleBooking({ selection: selectedOption });
    });
  };

  handleConfirmSelectedParts = () => {
    const { selectedOption } = this.state;

    /**
     * For immutability purposes make a copy of the object
     */
    const selectedOptionCopy = JSON.parse(JSON.stringify(selectedOption));

    const filteredExaminationParts = selectedOptionCopy.examinationParts.filter(
      (part) => part.checked
    );

    const missingExamParts = {
      licenceId: selectedOptionCopy.licenceId,
      examPartsId: selectedOptionCopy.examinationParts
        .filter((part) => !part.checked)
        .map((part) => part.examPartId)
    };

    // eslint-disable-next-line no-param-reassign
    filteredExaminationParts.forEach((part) => delete part.checked);
    selectedOptionCopy.examinationParts = filteredExaminationParts;

    this.handleBooking({
      selection: selectedOptionCopy,
      missingExamParts
    });
  };

  isLoadButtonEnabled = () => {
    const { totalResults } = this.props;
    const { currentPage } = this.state;

    const maxPages = Math.ceil(totalResults / this.pageSize);

    return currentPage < maxPages;
  };

  onLoadButtonClick = () => {
    const { loadMoreRows, rows } = this.props;
    // get the previous currentPage, before the setState being applied
    const { currentPage } = this.state;

    if (this.isLoadButtonEnabled()) {
      this.setState(
        (prevState) => ({ currentPage: prevState.currentPage + 1 }),
        () => {
          // eslint-disable-next-line react/destructuring-assignment
          if (this.state.currentPage * this.pageSize > rows.length) {
            return loadMoreRows({
              offset: currentPage * this.pageSize,
              pageSize: this.pageSize
            });
          }
        }
      );
    }
  };

  toggleModal = () => {
    this.setState((prevState) => ({
      showModal: !prevState.showModal
    }));
  };

  render() {
    const { areThereNoDates, rows, t, filter } = this.props;
    const {
      currentPage,
      isProceedButtonDisabled,
      remainingAvailableTime,
      selectedOption,
      showModal
    } = this.state;
    const isLoadButtonEnabled = this.isLoadButtonEnabled();

    return (
      <div className="col-sm-12" style={{ marginTop: 40 }}>
        <Modal
          cancelActionLabel={t(`modules.localization.register.generals.button.cancel`)}
          closeModal={this.toggleModal}
          confirmAction={this.handleConfirmSelectedParts}
          confirmActionDisabled={isProceedButtonDisabled}
          confirmActionLabel={t(`modules.localization.register.authenticated.exams.proceed`)}
          isOpen={showModal}
          title={t(`modules.localization.register.authenticated.exams.selectExaminationParts`)}
        >
          <SelectExamParts
            checkOption={this.handleCheckExamPart}
            item={selectedOption}
            remainingAvailableTime={remainingAvailableTime}
          />
        </Modal>
        <div style={styles.body}>
          {areThereNoDates && (
            <NoResults
              message={t(`modules.localization.register.authenticated.exams.emptyTable`)}
            />
          )}
          {filter === (`begin` || `both`) && (
            <Notice
              type="warning"
              message={t(`modules.localization.register.authenticated.exams.filtered`)}
            />
          )}
          {rows.slice(0, currentPage * this.pageSize).map((item) => (
            <Item key={cuid()} item={item} handleRowClick={this.handleRowClick} />
          ))}
          {filter === (`end` || `both`) && !isLoadButtonEnabled && (
            <Notice
              type="warning"
              message={t(`modules.localization.register.authenticated.exams.filtered`)}
            />
          )}
        </div>
        {isLoadButtonEnabled && <Footer onLoadButtonClick={this.onLoadButtonClick} />}
      </div>
    );
  }
}

BlockLoader.propTypes = {
  areThereNoDates: PropTypes.bool,
  clearMissingExamsParts: PropTypes.func.isRequired,
  filter: PropTypes.string.isRequired,
  loadMoreRows: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  setMissingExamsParts: PropTypes.func.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  totalResults: PropTypes.number.isRequired
};

const mapStateToProps = (state) => ({
  areThereNoDates: state.exams.areThereNoDates
});

const mapDispatchToProps = {
  clearMissingExamsParts,
  navigateTo,
  setMissingExamsParts,
  setSelectedDate
};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withNamespaces()
)(BlockLoader);
